import { Stack } from '@mui/material';
import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { AgTable } from '../../../components/AgTable/AgTable';
import { FadeInGridWrapper } from '../../../components/grid-renderers/FadeInGridWrapper';
import { ROUTES } from '../../../constants/RouteNameMapping';
import {
  CustomFieldsUploadStatusData,
  customFieldsUploadStatusDataSchema,
} from '../../../schemas/CustomFieldsUploadStatusData.schema';
import { useSchemaToGrid } from '../../../util/schema-utils';
import { StatusRenderer } from '../../../components/AgTable/cell-renderers/StatusRenderer';

export function UploadStatusGrid({ data }: { data: CustomFieldsUploadStatusData[] }) {
  const columnDefs = useUploadStatusColDefs();
  return (
    <FadeInGridWrapper>
      <Stack height='80vh' mt='1rem'>
        <AgTable
          columnDefs={columnDefs}
          rowData={data}
          defaultColDef={defaultColDef}
          rowGroupPanelShow='never'
        />
      </Stack>
    </FadeInGridWrapper>
  );
}

function useUploadStatusColDefs() {
  const schemaToGrid = useSchemaToGrid();
  return useMemo(() => {
    const schema = customFieldsUploadStatusDataSchema();
    const baseCols = schemaToGrid(schema.omit(['id', 'status', 'fields']));

    // Add default sorting to uploadedDate column
    const uploadedDateCol = baseCols.find((col) => col.field === 'uploadedDate');
    if (uploadedDateCol) {
      uploadedDateCol.initialSort = 'desc';
      uploadedDateCol.initialSortIndex = 0;
    }

    return baseCols.concat([idColDef, statusColDef]);
  }, [schemaToGrid]);
}

const idColDef: ColDef = {
  headerName: 'View',
  field: 'id',
  filter: true,
  cellRenderer: (params: ValueGetterParams) => (
    <Link to={`/${ROUTES.BULK_UPLOAD_REPORT}/${params.data?.id}`}>View Report</Link>
  ),
  pinned: 'right',
};

const statusColDef: ColDef = {
  headerName: 'Status',
  field: 'status',
  filter: true,
  cellRenderer: (params: ValueGetterParams) => (
    <StatusRenderer status={params.data?.status} value={params.data?.status} />
  ),
};

const defaultColDef = {
  flex: 1,
};
